/**
 * The invitation mail links to `<app>/#email=<address>` so the sign-in form
 * starts with the invited address (packages/api/src/utils/email.ts). The hash
 * is read once at start, before the router redirects to /login and drops it,
 * kept for this tab only, and removed from the address bar — an address in
 * the URL would end up in history and bookmarks.
 */
import { isValidEmail, normalizeEmail } from '@fsv/core';

const KEY = 'fsv.invite.email';

export function emailFromHash(hash: string): string | null {
  const m = /^#email=([^&]+)$/.exec(hash);
  if (!m) return null;
  let email: string;
  try {
    email = normalizeEmail(decodeURIComponent(m[1]!));
  } catch {
    return null;
  }
  return isValidEmail(email) ? email : null;
}

export function captureInvitePrefill(win: Pick<Window, 'location' | 'history' | 'sessionStorage'> = window): void {
  const email = emailFromHash(win.location.hash);
  if (!email) return;
  try {
    win.sessionStorage.setItem(KEY, email);
  } catch {
    /* private mode: the form just starts empty */
  }
  win.history.replaceState(win.history.state, '', `${win.location.pathname}${win.location.search}`);
}

/** Read only — a state initializer runs twice under StrictMode; `clearInvitePrefill` in an effect. */
export function peekInvitePrefill(storage: Pick<Storage, 'getItem'> = window.sessionStorage): string {
  try {
    return storage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

/** After the form took it: a second visit starts empty. */
export function clearInvitePrefill(storage: Pick<Storage, 'removeItem'> = window.sessionStorage): void {
  try {
    storage.removeItem(KEY);
  } catch {
    /* nothing kept */
  }
}

/** After a club's sign-up (0024): the sign-in form starts with the address that just became the club's admin. */
export function setInvitePrefill(email: string, storage: Pick<Storage, 'setItem'> = window.sessionStorage): void {
  try {
    storage.setItem(KEY, normalizeEmail(email));
  } catch {
    /* the form just starts empty */
  }
}
